<script setup lang="ts">
/**
 * Top bar — brief 09-layout: "Brand/title/document controls only. Do not put
 * drawing controls here. Export should be visible... New/open/settings can go
 * in overflow." Drawing tools moved to ToolStrip.vue.
 */
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
	IconDotsVertical,
	IconDownload,
	IconFilePlus,
	IconFolders,
	IconGridDots,
	IconHelp,
	IconLock,
	IconMoon,
	IconSettings,
	IconSun,
	IconX,
} from '@tabler/icons-vue'
import AppLogo from '@/components/AppLogo.vue'
import { useEntitlementStore } from '@/stores/entitlement'
import { useLayersStore } from '@/stores/layers'
import { usePatternStore } from '@/stores/pattern'
import { useSketchbookStore } from '@/stores/sketchbook'
import { useUiStore } from '@/stores/ui'

const layers = useLayersStore()
const entitlement = useEntitlementStore()
const patternStore = usePatternStore()
const sketchbook = useSketchbookStore()
const ui = useUiStore()

/** Save the current sketch, then start a fresh blank one. */
async function newSketch(): Promise<void> {
	if (sketchbook.currentSketchId && layers.isDirty)
		await sketchbook.saveCurrent()
	await sketchbook.newSketch(
		patternStore.currentPatternId,
		patternStore.currentVariantId,
	)
	overflowOpen.value = false
}

const nameDraft = ref(sketchbook.currentName)
watch(
	() => sketchbook.currentName,
	(n) => (nameDraft.value = n),
)

function openSavedSketches(): void {
	ui.showGallery = true
	overflowOpen.value = false
}

function openSettings(): void {
	ui.showSettings = true
	overflowOpen.value = false
}

function commitName(): void {
	const name = nameDraft.value.trim()
	if (!name || name === sketchbook.currentName) {
		nameDraft.value = sketchbook.currentName
		return
	}
	void sketchbook.renameCurrent(name)
}

function blurTarget(e: KeyboardEvent): void {
	;(e.target as HTMLInputElement).blur()
}

// Export is no longer entirely paid-only (brief 12-vector_draw: free mode
// gets wallpaper export at 2×2/3×3) — the sheet itself always opens; PSD,
// vector, and larger wallpaper sizes show their own lock inside it.
function onExport(): void {
	ui.showExport = true
}

function onRenderPsd(): void {
	if (!entitlement.canExport) {
		ui.openPaywall('export')
		return
	}
	ui.showRenderPsd = true
	overflowOpen.value = false
}

// "New/open/settings can go in overflow" — a small dropdown off a kebab
// button, closed on an outside click or after picking an action.
const overflowOpen = ref(false)
const overflowRef = ref<HTMLElement | null>(null)

function onDocClick(e: MouseEvent): void {
	if (overflowOpen.value && overflowRef.value && !overflowRef.value.contains(e.target as Node))
		overflowOpen.value = false
}

onMounted(() => document.addEventListener('click', onDocClick))
onBeforeUnmount(() => document.removeEventListener('click', onDocClick))
</script>

<template>
	<div class="toolbar">
		<div class="brand">
			<AppLogo :size="24" />
			<span class="brand-name">Escherllate</span>
		</div>

		<input
			v-model="nameDraft"
			class="sketch-name"
			type="text"
			aria-label="Sketch name"
			spellcheck="false"
			maxlength="60"
			@blur="commitName"
			@keydown.enter="blurTarget"
		/>

		<div class="group right">
			<button class="tb-btn" title="Export" @click="onExport">
				<IconDownload :size="16" stroke-width="1.75" />
				<span>Export</span>
			</button>

			<button
				class="tb-btn icon-only"
				:title="ui.theme === 'dark' ? 'Light mode' : 'Dark mode'"
				@click="ui.toggleTheme()"
			>
				<IconSun v-if="ui.theme === 'dark'" :size="16" stroke-width="1.75" />
				<IconMoon v-else :size="16" stroke-width="1.75" />
			</button>
			<button class="tb-btn icon-only" title="Help" @click="ui.openHelp()">
				<IconHelp :size="16" stroke-width="1.75" />
			</button>

			<div ref="overflowRef" class="overflow">
				<button
					class="tb-btn icon-only"
					title="More"
					:aria-expanded="overflowOpen"
					@click="overflowOpen = !overflowOpen"
				>
					<IconX v-if="overflowOpen" :size="16" stroke-width="1.75" />
					<IconDotsVertical v-else :size="16" stroke-width="1.75" />
				</button>
				<div v-if="overflowOpen" class="overflow-menu" role="menu">
					<button class="menu-item" @click="newSketch">
						<IconFilePlus :size="16" stroke-width="1.75" />
						<span>New sketch</span>
					</button>
					<button class="menu-item" @click="openSavedSketches">
						<IconFolders :size="16" stroke-width="1.75" />
						<span>Saved sketches</span>
					</button>
					<button class="menu-item" @click="openSettings">
						<IconSettings :size="16" stroke-width="1.75" />
						<span>Settings</span>
					</button>
					<button class="menu-item" @click="onRenderPsd">
						<IconGridDots :size="16" stroke-width="1.75" />
						<span>Render PSD</span>
						<IconLock
							v-if="!entitlement.canExport"
							:size="13"
							stroke-width="2"
							class="lock"
						/>
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.toolbar {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
	padding: 6px 10px;
	background: var(--ui-surface);
	border-bottom: 1px solid var(--ui-border);
}

.brand {
	display: flex;
	align-items: center;
	gap: 8px;
}

.brand-name {
	font-family: var(--font-mono);
	font-size: 13px;
	letter-spacing: 0.06em;
	color: var(--text-primary);
}

.sketch-name {
	width: 170px;
	min-height: 30px;
	padding: 3px 9px;
	font-size: 15px;
	font-weight: 700;
	color: var(--text-primary);
	background: transparent;
	border: 1px solid transparent;
	border-radius: 7px;
	transition:
		border-color 0.12s,
		background 0.12s;
	border-color: var(--ui-border);
	opacity: 0.8;
}

.sketch-name:hover {
	opacity: 1;
}

.sketch-name:focus {
	opacity: 1;
	outline: none;
	border-color: var(--accent);
	background: var(--canvas-bg);
}

.group {
	display: flex;
	align-items: center;
	gap: 6px;
}

.group.right {
	margin-left: auto;
}

.tb-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	min-height: 32px;
	padding: 5px 11px;
	font-size: 12.5px;
	background: transparent;
	color: var(--text-primary);
	border: 1px solid var(--ui-border);
	border-radius: 8px;
	cursor: pointer;
	transition:
		border-color 0.12s,
		background 0.12s;

		svg {
			pointer-events: none;
		}
}

.tb-btn.icon-only {
	padding: 5px 8px;
}

.tb-btn:hover:not(:disabled) {
	border-color: var(--accent);
	background: color-mix(in srgb, var(--accent) 10%, transparent);
}

.lock {
	color: var(--text-muted);
}

.overflow {
	position: relative;
}

.overflow-menu {
	position: absolute;
	top: calc(100% + 6px);
	right: 0;
	z-index: 50;
	display: flex;
	flex-direction: column;
	min-width: 180px;
	padding: 6px;
	gap: 2px;
	background: var(--ui-surface);
	border: 1px solid var(--ui-border);
	border-radius: 10px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
	z-index: 100;
}

.menu-item {
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	min-height: 34px;
	padding: 6px 10px;
	font-size: 13px;
	text-align: left;
	color: var(--text-primary);
	background: transparent;
	border: none;
	border-radius: 7px;
	cursor: pointer;
}

.menu-item:hover {
	background: color-mix(in srgb, var(--accent) 12%, transparent);
}

@media (max-width: 730px) {
	.toolbar {
		gap: 6px;
		padding: 4px 8px;
	}

	.brand-name {
		display: none;
	}

	.sketch-name {
		width: 120px;
	}

	.tb-btn span {
		display: none;
	}

	.menu-item span {
		display: inline;
	}
}
</style>
